import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Hourglass, Sliders, MusicNotes, Lightning, Waveform,
  Play, Stop, Plus, X, PaperPlaneTilt, ArrowRight, Sparkle, CheckCircle,
  Download, FileText, ArrowLineRight,
} from "@phosphor-icons/react";

const KEYS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const MODES = ["major", "minor", "dorian", "phrygian", "lydian", "mixolydian", "aeolian", "locrian"];
const TIME_SIGS = ["3/4", "4/4", "5/4", "6/8", "7/8", "12/8"];
const STRUCTURE_BLOCKS = ["intro", "verse", "pre-chorus", "chorus", "bridge", "solo", "drop", "outro"];
const PRESETS = ["I-V-vi-IV", "vi-IV-I-V", "I-IV-V", "ii-V-I", "I-vi-IV-V", "i-VII-VI-VII", "i-iv-V", "Am-F-C-G", "Cmaj7-Dm7-Em7-Fmaj7"];

const HOUR = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const STEPS = [
  { id: "lyrics",   label: "1. Letra",      icon: FileText },
  { id: "harmony",  label: "2. Armonía",    icon: Waveform },
  { id: "structure",label: "3. Estructura", icon: Sliders },
  { id: "sound",    label: "4. Sonido",     icon: MusicNotes },
  { id: "review",   label: "5. Revisar",    icon: PaperPlaneTilt },
];

export default function Studio() {
  const nav = useNavigate();
  const { user } = useAuth();

  // ----- Session -----
  const [balance, setBalance] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);
  const heartbeatRef = useRef(null);
  const tickerRef = useRef(null);

  // ----- Wizard -----
  const [step, setStep] = useState("lyrics");

  // ----- Form -----
  const [lyricsMode, setLyricsMode] = useState("manual"); // manual | assist
  const [fullLyrics, setFullLyrics] = useState("");
  const [lyricsPrompt, setLyricsPrompt] = useState("");
  const [assistLoading, setAssistLoading] = useState(false);
  const [key, setKey] = useState("C");
  const [mode, setMode] = useState("major");
  const [bpm, setBpm] = useState(110);
  const [timeSig, setTimeSig] = useState("4/4");
  const [chordProgression, setChordProgression] = useState("I-V-vi-IV");
  const [structure, setStructure] = useState(["intro", "verse", "chorus", "verse", "chorus", "bridge", "chorus", "outro"]);
  const [genreHint, setGenreHint] = useState("");
  const [instrumentation, setInstrumentation] = useState("");
  const [mood, setMood] = useState("");
  const [title, setTitle] = useState("");
  const [artistName, setArtistName] = useState(user?.name || "");

  // ----- Output -----
  const [generating, setGenerating] = useState(false);
  const [songs, setSongs] = useState([]);

  // ----- LIFECYCLE -----
  useEffect(() => {
    if (!user) { nav("/login"); return; }
    refreshBalance();
    loadMySongs();
    return () => cleanupSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onUnload = () => {
      if (sessionId) {
        navigator.sendBeacon?.(
          `${process.env.REACT_APP_BACKEND_URL}/api/studio/session/end`,
          new Blob([JSON.stringify({ session_id: sessionId })], { type: "application/json" }),
        );
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [sessionId]);

  const refreshBalance = async () => {
    const { data } = await api.get("/studio/balance");
    setBalance(data.minutes || 0);
  };

  const loadMySongs = async () => {
    try {
      const { data } = await api.get("/studio/my-songs");
      setSongs(data.songs || []);
      // Re-poll any still-generating
      (data.songs || []).filter(s => s.status === "generating_audio").forEach(s => pollSong(s.id));
    } catch (e) {
      console.warn("loadMySongs failed:", e);
    }
  };

  const startSession = async () => {
    try {
      const { data } = await api.post("/studio/session/start");
      setSessionId(data.session_id);
      setBalance(data.minutes_balance);
      setElapsed(0); elapsedRef.current = 0;
      tickerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
      heartbeatRef.current = setInterval(async () => {
        try {
          const { data: hb } = await api.post("/studio/session/heartbeat", { session_id: data.session_id });
          setBalance(hb.minutes_balance);
          if (hb.out_of_time) {
            toast.warning("Se acabó tu tiempo. Sesión cerrada.");
            stopSession(false);
          }
        } catch (e) {
          console.warn("heartbeat failed:", e);
        }
      }, 60000);
      toast.success("Sesión iniciada · el reloj corre");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la sesión");
      if (e?.response?.status === 402) nav("/studio/buy");
    }
  };

  const stopSession = async (notify = true) => {
    const sid = sessionId;
    cleanupSession();
    if (sid) {
      try {
        await api.post("/studio/session/end", { session_id: sid });
      } catch (e) {
        console.warn("session end failed:", e);
      }
    }
    setSessionId(null);
    refreshBalance();
    if (notify) toast.info("Sesión cerrada.");
  };

  const cleanupSession = () => {
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
  };

  // ----- LYRICS ASSIST (optional Claude call) -----
  const assistLyrics = async () => {
    if (!sessionId) return toast.error("Inicia la sesión primero");
    if (lyricsPrompt.trim().length < 20) return toast.error("Describe la idea en al menos 20 caracteres");
    setAssistLoading(true);
    try {
      const { data } = await api.post("/studio/lyrics-assist", {
        session_id: sessionId, prompt: lyricsPrompt,
        structure, key, mode, bpm, time_signature: timeSig,
      });
      setFullLyrics(data.lyrics || "");
      setLyricsMode("manual");  // jump to editor so creator can refine
      toast.success("Letra generada. Edítala como gustes.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando letra");
    } finally { setAssistLoading(false); }
  };

  // ----- STRUCTURE -----
  const addBlock = (b) => setStructure(s => [...s, b]);
  const removeBlock = (i) => setStructure(s => s.filter((_, j) => j !== i));

  // ----- GENERATE (the only call that triggers Suno) -----
  const generate = async () => {
    if (!sessionId) return toast.error("Inicia la sesión primero");
    if (!artistName.trim()) return toast.error("Falta el nombre de artista");
    const hasManual = fullLyrics.trim().length >= 40;
    const hasPrompt = lyricsPrompt.trim().length >= 20;
    if (!hasManual && !hasPrompt) return toast.error("Escribe la letra o describe la idea (paso 1).");
    setGenerating(true);
    try {
      const { data } = await api.post("/studio/generate", {
        session_id: sessionId,
        title: title || null,
        artist_name: artistName,
        key, mode, bpm, time_signature: timeSig,
        chord_progression: chordProgression || null,
        structure,
        instrumentation: instrumentation || null,
        genre_hint: genreHint || null,
        mood: mood || null,
        full_lyrics: hasManual ? fullLyrics : null,
        lyrics_prompt: !hasManual ? lyricsPrompt : null,
      });
      toast.success(`"${data.title}" se está generando…`);
      setSongs(s => [{ id: data.song_id, title: data.title, status: "generating_audio", lyrics_by_ai: data.lyrics_by_ai }, ...s]);
      pollSong(data.song_id);
      setStep("lyrics"); // ready for next song
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando");
    } finally { setGenerating(false); }
  };

  const pollSong = async (songId) => {
    const tick = async () => {
      try {
        const { data } = await api.get(`/songs/${songId}/audio-status`);
        setSongs(arr => arr.map(s => s.id === songId ? { ...s, ...data } : s));
        if (data.status === "audio_ready" || data.status === "audio_failed") return;
      } catch (e) {
        console.warn("audio-status poll failed:", e);
      }
      setTimeout(tick, 5000);
    };
    setTimeout(tick, 5000);
  };

  const insufficient = !sessionId && balance <= 0;
  const audioUrl = (s) => s.audio_url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${s.audio_url}` : s.audio_url;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* HEADER */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#050505]/85 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/" className="text-white/50 hover:text-white p-2 -ml-2 rounded-full" data-testid="studio-back">
            <ArrowLeft size={18} weight="bold" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FFB800] flex items-center justify-center">
              <Sliders weight="fill" size={16} className="text-black" />
            </div>
            <div className="leading-tight">
              <p className="text-xs font-display font-bold">Studio Pro</p>
              <p className="text-[10px] uppercase tracking-widest text-[#FFB800]">{artistName || "Sesión"}</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <div className="px-3 py-1.5 rounded-full bg-white/5 text-xs flex items-center gap-1.5" data-testid="studio-balance-pill">
              <Hourglass size={12} weight="fill" className="text-[#B4F34A]" />
              <span className="text-white/60">Saldo:</span>
              <span className="font-display font-bold text-[#B4F34A]">{balance} min</span>
            </div>
            <div className="px-3 py-1.5 rounded-full bg-[#FFB800]/10 text-xs flex items-center gap-1.5" data-testid="studio-timer">
              <span className="text-white/60">Sesión:</span>
              <span className="font-mono font-bold text-[#FFB800]">{HOUR(elapsed)}</span>
            </div>
            {!sessionId ? (
              <button onClick={startSession} disabled={insufficient} className="px-4 py-1.5 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] disabled:opacity-40 text-black font-bold text-xs flex items-center gap-1" data-testid="studio-start">
                <Play weight="fill" size={12} /> Iniciar sesión
              </button>
            ) : (
              <button onClick={() => stopSession()} className="px-4 py-1.5 rounded-full bg-[#FF4B00] hover:bg-[#E64400] text-black font-bold text-xs flex items-center gap-1" data-testid="studio-stop">
                <Stop weight="fill" size={12} /> Finalizar
              </button>
            )}
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {insufficient && (
          <div className="mb-5 rounded-2xl bg-[#FF4B00]/10 border border-[#FF4B00]/30 p-5 flex items-center gap-3 flex-wrap" data-testid="studio-no-balance">
            <Hourglass weight="fill" size={22} className="text-[#FF4B00]" />
            <div className="flex-1 min-w-0">
              <p className="font-bold">No tienes tiempo de estudio</p>
              <p className="text-xs text-white/60">Compra un paquete para empezar. $8 MXN/min.</p>
            </div>
            <Link to="/studio/buy" className="px-4 py-2 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-bold text-sm" data-testid="studio-cta-buy">
              Comprar tiempo
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* ============== LEFT: WIZARD ============== */}
          <div>
            {/* Steps tabs */}
            <div className="rounded-2xl bg-[#0f0f0f] border border-white/5 p-1.5 mb-5 flex flex-wrap gap-1" data-testid="studio-wizard-tabs">
              {STEPS.map(s => {
                const Active = step === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setStep(s.id)}
                    className={`flex-1 min-w-[110px] px-3 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${Active ? "bg-[#FFB800] text-black" : "text-white/60 hover:bg-white/5"}`}
                    data-testid={`step-${s.id}`}
                  >
                    <s.icon weight={Active ? "fill" : "regular"} size={14} />
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{s.label.split(". ")[1]}</span>
                  </button>
                );
              })}
            </div>

            {/* STEP 1: LYRICS */}
            {step === "lyrics" && (
              <Card title="Tu letra" icon={FileText} subtitle="Escríbela tú mismo (sin costo extra) o pide ayuda a la IA.">
                <div className="flex gap-2 mb-4" data-testid="lyrics-mode">
                  <button
                    onClick={() => setLyricsMode("manual")}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${lyricsMode === "manual" ? "bg-[#B4F34A]/15 border-[#B4F34A] text-[#B4F34A]" : "bg-[#0a0a0a] border-white/10 text-white/60"}`}
                    data-testid="lyrics-mode-manual"
                  >
                    Yo la escribo (gratis)
                  </button>
                  <button
                    onClick={() => setLyricsMode("assist")}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${lyricsMode === "assist" ? "bg-[#FFB800]/15 border-[#FFB800] text-[#FFB800]" : "bg-[#0a0a0a] border-white/10 text-white/60"}`}
                    data-testid="lyrics-mode-assist"
                  >
                    Asistente IA
                  </button>
                </div>

                {lyricsMode === "manual" ? (
                  <>
                    <p className="text-xs text-white/50 mb-2">
                      Tip: usa los tags <span className="font-mono text-[#FFB800]">[Intro] [Verse] [Chorus] [Bridge] [Outro]</span> para marcar las secciones (la IA respeta tu estructura).
                    </p>
                    <textarea
                      value={fullLyrics}
                      onChange={(e) => setFullLyrics(e.target.value)}
                      rows={16}
                      placeholder={"[Intro]\n(instrumental)\n\n[Verse]\nEsa noche en la avenida...\n\n[Chorus]\n..."}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none resize-none font-mono text-sm leading-relaxed"
                      data-testid="lyrics-manual-textarea"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-white/40">{fullLyrics.length} caracteres · mín 40</p>
                      <button onClick={() => setStep("harmony")} className="text-sm text-[#B4F34A] hover:underline flex items-center gap-1" data-testid="lyrics-next">
                        Siguiente: Armonía <ArrowLineRight size={14} />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-white/50 mb-2">
                      Describe la idea. La IA escribirá la letra respetando tu estructura.
                      <span className="text-[#FFB800]"> Esta acción sí consume créditos de IA.</span>
                    </p>
                    <textarea
                      value={lyricsPrompt}
                      onChange={(e) => setLyricsPrompt(e.target.value)}
                      rows={6}
                      placeholder="Una noche fría en el barrio, recuerdo del primer amor, esperanza a pesar de la distancia..."
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none resize-none text-sm"
                      data-testid="lyrics-prompt"
                    />
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <button
                        onClick={assistLyrics}
                        disabled={!sessionId || assistLoading || lyricsPrompt.trim().length < 20}
                        className="flex-1 min-w-[180px] py-2.5 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-40 text-black font-bold text-sm flex items-center justify-center gap-2"
                        data-testid="lyrics-assist-btn"
                      >
                        <Sparkle weight="fill" size={14} />
                        {assistLoading ? "Generando…" : "Generar letra con IA"}
                      </button>
                      <button onClick={() => setStep("harmony")} className="text-sm text-[#B4F34A] hover:underline flex items-center gap-1 px-3">
                        Saltar a Armonía <ArrowLineRight size={14} />
                      </button>
                    </div>
                  </>
                )}
              </Card>
            )}

            {/* STEP 2: HARMONY */}
            {step === "harmony" && (
              <Card title="Armonía y métrica" icon={Waveform} subtitle="Define la tonalidad, el tempo y la progresión.">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <Selector label="Tonalidad" value={key} onChange={setKey} options={KEYS} testid="studio-key" />
                  <Selector label="Modo" value={mode} onChange={setMode} options={MODES} testid="studio-mode" />
                  <NumInput label="BPM" value={bpm} onChange={setBpm} min={40} max={240} testid="studio-bpm" />
                  <Selector label="Compás" value={timeSig} onChange={setTimeSig} options={TIME_SIGS} testid="studio-timesig" />
                </div>
                <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Progresión de acordes</p>
                <input
                  value={chordProgression}
                  onChange={(e) => setChordProgression(e.target.value)}
                  placeholder="Ej: I-V-vi-IV   o   Am-F-C-G   o   Cmaj7-Dm7-Em7-Fmaj7"
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none font-mono text-sm"
                  data-testid="studio-chords"
                />
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {PRESETS.map(p => (
                    <button key={p} onClick={() => setChordProgression(p)} className="text-[10px] px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/15 font-mono">{p}</button>
                  ))}
                </div>
                <StepNav onPrev={() => setStep("lyrics")} onNext={() => setStep("structure")} />
              </Card>
            )}

            {/* STEP 3: STRUCTURE */}
            {step === "structure" && (
              <Card title="Estructura de la canción" icon={Sliders} subtitle="Estos bloques se mandan a la IA en orden. La letra y el audio los respetan.">
                <div className="flex flex-wrap gap-2 mb-3" data-testid="studio-structure">
                  {structure.map((b, i) => (
                    <div key={`${b}-${i}`} className="px-3 py-1.5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/30 text-xs font-semibold text-[#FFB800] flex items-center gap-1.5">
                      {b}
                      <button onClick={() => removeBlock(i)} className="text-[#FFB800]/70 hover:text-white"><X size={12} weight="bold" /></button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Agregar</p>
                <div className="flex flex-wrap gap-1.5">
                  {STRUCTURE_BLOCKS.map(b => (
                    <button key={b} onClick={() => addBlock(b)} className="text-xs px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/15 flex items-center gap-1" data-testid={`add-block-${b}`}>
                      <Plus size={10} /> {b}
                    </button>
                  ))}
                </div>
                <StepNav onPrev={() => setStep("harmony")} onNext={() => setStep("sound")} />
              </Card>
            )}

            {/* STEP 4: SOUND */}
            {step === "sound" && (
              <Card title="Sonido e instrumentación" icon={MusicNotes} subtitle="Describe el timbre y la sensación.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FieldText label="Género / referencia" value={genreHint} setValue={setGenreHint} placeholder="synthwave, neo-soul, indie folk" testid="studio-genre" />
                  <FieldText label="Mood" value={mood} setValue={setMood} placeholder="melancólico, fiestero, motivacional" testid="studio-mood" />
                </div>
                <FieldText className="mt-3" label="Instrumentación" value={instrumentation} setValue={setInstrumentation} placeholder="sintetizador analógico de los 80, bajo eléctrico fuzz, batería acústica con reverb" testid="studio-instrumentation" multiline />
                <StepNav onPrev={() => setStep("structure")} onNext={() => setStep("review")} />
              </Card>
            )}

            {/* STEP 5: REVIEW + GENERATE */}
            {step === "review" && (
              <Card title="Revisar y generar" icon={PaperPlaneTilt} subtitle="Confirma todo antes de gastar el cómputo de la IA.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <FieldText label="Título (opcional)" value={title} setValue={setTitle} placeholder="Se infiere si lo dejas vacío" testid="studio-title" />
                  <FieldText label="Nombre de artista" value={artistName} setValue={setArtistName} placeholder="Cómo aparecerá en la canción" testid="studio-artist" />
                </div>

                <div className="rounded-2xl bg-black/40 border border-white/10 p-4 mb-4 space-y-2 text-sm">
                  <Row label="Letra" value={fullLyrics.trim() ? `Manual · ${fullLyrics.length} chars` : (lyricsPrompt.trim() ? "Generará IA · " + lyricsPrompt.length + " chars de prompt" : "—")} />
                  <Row label="Tonalidad" value={`${key} ${mode}`} />
                  <Row label="BPM / Compás" value={`${bpm} BPM · ${timeSig}`} />
                  <Row label="Progresión" value={chordProgression || "—"} mono />
                  <Row label="Estructura" value={structure.join(" → ")} />
                  <Row label="Género" value={genreHint || "—"} />
                  <Row label="Instrumentación" value={instrumentation || "—"} />
                </div>

                <div className="rounded-xl bg-[#FFB800]/5 border border-[#FFB800]/20 p-3 mb-4 text-xs text-white/75 leading-relaxed">
                  Al pulsar <span className="text-[#FFB800] font-bold">Generar canción</span> arrancamos Suno (audio) y, si dejaste la letra a la IA, también Claude. Tu sesión sigue cobrando los minutos por separado.
                </div>

                <button
                  onClick={generate}
                  disabled={!sessionId || generating || insufficient}
                  className="w-full py-5 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-40 text-black font-bold text-base flex items-center justify-center gap-2 active:scale-95 transition-all"
                  data-testid="studio-generate"
                >
                  {generating ? "Enviando a la IA…" : sessionId ? "Generar canción" : "Inicia la sesión arriba"}
                  <PaperPlaneTilt weight="fill" size={18} />
                </button>
                <button onClick={() => setStep("sound")} className="mt-3 text-sm text-white/50 hover:text-white block mx-auto">← Volver a Sonido</button>
              </Card>
            )}
          </div>

          {/* ============== RIGHT: MY SONGS + TIPS ============== */}
          <aside className="space-y-4">
            <Card title="Mis generaciones" icon={MusicNotes} compact>
              {songs.length === 0 ? (
                <p className="text-sm text-white/40">Aquí aparecerán tus canciones del estudio.</p>
              ) : (
                <div className="space-y-2.5 max-h-[480px] overflow-y-auto pr-1" data-testid="studio-songs">
                  {songs.map(s => (
                    <SongRow key={s.id} song={s} audioUrl={audioUrl} />
                  ))}
                </div>
              )}
            </Card>

            <Card title="Cómo funciona" icon={Hourglass} compact>
              <ul className="text-xs text-white/65 space-y-1.5 leading-relaxed">
                <li>· Saldo se cobra solo cuando la sesión está <span className="text-[#B4F34A]">activa</span>.</li>
                <li>· <span className="text-[#FFB800] font-semibold">1 minuto</span> cada 60s ($8 MXN/min).</li>
                <li>· Pulsa <span className="text-[#FF4B00]">Finalizar</span> al terminar.</li>
                <li>· Letra escrita por ti = <span className="text-[#B4F34A]">sin costo extra de IA</span>.</li>
                <li>· Al pulsar Generar, se invoca Suno (siempre) y Claude (solo si elegiste asistente).</li>
              </ul>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ---------------- Subcomponents ----------------
function Card({ title, icon: Icon, subtitle, children, compact }) {
  return (
    <section className={`rounded-2xl bg-[#0f0f0f] border border-white/5 ${compact ? "p-4" : "p-5 sm:p-6"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} weight="duotone" className="text-[#FFB800]" />
        <h3 className="font-display font-bold">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-white/50 mb-4 -mt-1">{subtitle}</p>}
      {children}
    </section>
  );
}

function StepNav({ onPrev, onNext }) {
  return (
    <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
      <button onClick={onPrev} className="text-sm text-white/50 hover:text-white px-3 py-1.5">← Atrás</button>
      <button onClick={onNext} className="text-sm text-[#B4F34A] hover:underline px-3 py-1.5 flex items-center gap-1" data-testid="step-next">
        Siguiente <ArrowLineRight size={14} />
      </button>
    </div>
  );
}

function Selector({ label, value, onChange, options, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</p>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none text-sm" data-testid={testid}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function NumInput({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</p>
      <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))} className="w-full px-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none text-sm font-mono" data-testid={testid} />
    </div>
  );
}

function FieldText({ label, value, setValue, placeholder, testid, multiline, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</p>
      {multiline ? (
        <textarea value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} rows={2} className="w-full px-4 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none text-sm resize-none" data-testid={testid} />
      ) : (
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} className="w-full px-4 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] outline-none text-sm" data-testid={testid} />
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-white/50 text-xs uppercase tracking-widest shrink-0">{label}</span>
      <span className={`text-white text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function SongRow({ song, audioUrl }) {
  return (
    <div className="rounded-xl bg-black/40 border border-white/10 p-3" data-testid={`song-row-${song.id}`}>
      <div className="flex items-start gap-3">
        {song.cover_url ? (
          <img src={song.cover_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-[#222] flex items-center justify-center shrink-0">
            <MusicNotes size={18} className="text-white/20" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm truncate">{song.title}</p>
          <p className="text-[10px] text-white/40">
            {song.status === "audio_ready" ? <span className="text-[#B4F34A]">● Listo</span> :
              song.status === "audio_failed" ? <span className="text-[#FF4B00]">● Error</span> :
                <span className="text-[#FFB800]">● Generando…</span>}
            {song.lyrics_by_ai && <span className="ml-1.5 text-white/30">· letra IA</span>}
          </p>
          {song.status === "audio_ready" && song.audio_url && (
            <audio controls src={audioUrl(song)} className="mt-1.5 w-full" style={{ height: "30px" }} />
          )}
          {song.status === "audio_ready" && (
            <div className="flex gap-2 mt-1.5">
              <Link to={`/song/${song.id}`} className="text-[10px] text-[#B4F34A] hover:underline flex items-center gap-1">
                Abrir <ArrowRight size={10} />
              </Link>
              {song.audio_url && (
                <a href={audioUrl(song)} download className="text-[10px] text-[#FFB800] hover:underline flex items-center gap-1">
                  <Download size={10} /> MP3
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
